/* eslint-disable max-lines-per-function */
import { useState, useEffect, useLayoutEffect, useMemo } from 'react'
import '@/styles/globals.css'
import '@/styles/sprite.css'
import Head from 'next/head'
import { useRouter } from 'next/router'
/** we might not need this when we use sentry's inbuilt error boundary
 * research and see while adding sentry
 */
// eslint-disable-next-line import/no-extraneous-dependencies
import { deleteCookie, setCookie } from 'cookies-next'
import type { NextWebVitalsMetric } from 'next/app'
import * as Lockr from 'lockr'
import { ErrorBoundary } from 'react-error-boundary'
// import { GetServerSidePropsContext } from 'next'
// import useragent from 'express-useragent'
import dynamic from 'next/dynamic'
import Cookies from 'js-cookie'
import omit from 'lodash/omit'
import SomethingWentWrong from '@/components/Common/SomethingWentWrong'
import AppLayout from '@/components/Common/AppLayout'
import ProgressLoading from '@/components/Common/ProgressLoading'
import getNextAPIURL from '@/utils/api-utils/get-next-api-url'

// import KycDataHandler from '@/components/kyc/common/DataHandler'
import getRouteNameFromUrl from '@/components/kyc/utils/get-route-name-from-url'
import { allKycScreenIds } from '@/components/kyc/utils/config'
import KycCommonUIStateProvider from '@/components/kyc/common/CommonUIState'
import { dnsPrefetchLinks } from '@/root/constants/universal-head-links'
import CommonStateProvider from '@/root/state/common/provider'

import UserProvider from '@/components/Common/user-provider/user-provider'
import GlobalExternalScripts from '@/scripts/global-scripts'
import { getIsMobileClient } from '@/root/utils/common/web/get-is-mobile-client'
import { newrelicCustomEvents } from '@/root/utils/common/custom/newrelic-custom-events'
import FullScreenCandlesChart from '@/components/kyc/common/FullScreenCandleStick'
import NatsProviderWrapper from '@/components/Common/NatsProvider/NatsProviderWrapper'
import SEOHeader from '@/components/Common/SEOHeader'
import { INDSTOCKS_HOST } from '@/root/constants/common'
import { getApiGatewayUrl } from '@/root/utils/network-adapter/api-gateway'
import { clearCookies, clearLocalStorage } from '@/root/utils/logout-utils'
import { fetchPOSTRequest } from '@/root/utils/network-adapter/fetch-request'
import { addParameterToURL } from '@/root/helpers'
import { SHIELD_URL } from '@/root/api/apiConstants'
import { getIsMobileDevice } from '@/root/utils/common/web/get-mobile-os'
import { SESSION_ID_COOKIE_NAME } from '@/root/utils/login/get-session-id'
import { logNewRelicEvent } from '@/root/utils/common/custom/newrelic-page-events'
import { devLog } from '@/root/utils/devLogger'
// import { useRemoveExistingPaymentQueryParamsOnFreshLoad } from '@/revamp/pods/payments/hooks/use-remove-payment-query-params-on-fresh-load'
const KycDataHandler = dynamic(
  () => import('@/components/kyc/common/DataHandler'),
)

const FinvuScript = dynamic(
  () => import('@/components/account-aggregator/FinViewScript'),
)

const Toast = dynamic(
  () => import('@/stories/components/Common/Toast/ToastContainer'),
  {
    ssr: false,
  },
)

// const SISTER_URL_QUERY_KEY = 'sister_url'

// eslint-disable-next-line max-lines-per-function
export default function MyApp({ props, Component, pageProps }) {
  const { platform, authorization, host, redirectTo } = props

  const router = useRouter()

  const sisterDomain = useMemo(
    () => props.redirectTo ?? router.query?.redirectTo?.toString() ?? '',
    [router.query, redirectTo],
  )

  useLayoutEffect(() => {
    devLog('sisterDomain', sisterDomain)
    if (sisterDomain) {
      const token = Cookies.get('token')
      const refreshToken = Cookies.get('refresh_token')
      logNewRelicEvent('web_host_cookies_set_on_redirection', {
        host,
        is_token_present: !!token,
        is_refresh_token_present: !!refreshToken,
        return_to: sisterDomain,
      })
      window.location.href = sisterDomain
    }
  }, [host, sisterDomain])

  const [referrer, setReferrer] = useState('')
  const [state, setState] = useState<{
    routeState: 'idle' | 'pending' | 'resolved'
    loadingKey: number
  }>({
    routeState: 'idle',
    loadingKey: 0,
  })

  const { query } = router
  useEffect(() => {
    if (query.logout) {
      clearLocalStorage()
      clearCookies()
    }
  }, [query.logout])

  useEffect(() => {
    if (!query.logout) {
      const token = Cookies.get('token')
      const refreshToken = Cookies.get('refresh_token')
      if (token) {
        Lockr.set('token', token)
      }
      if (refreshToken) {
        Lockr.set('refresh_token', refreshToken)
      }
      const memberId = Cookies.get('memberId')
      if (memberId && typeof window !== 'undefined') {
        window.localStorage.setItem('memberId', memberId)
      }
    }
  }, [])

  useEffect(() => {
    // Lazy load Sentry and initialize it
    import('src/utils/sentry')
      .then(sentry => {
        window.sentry = sentry.default
        window.sentry.initializeSentry()
      })
      .catch(error => console.error(error))
  }, [])

  useEffect(() => {
    setReferrer(document.referrer)

    // A page session lasts as long as the tab or the browser is open,
    // and survives over page reloads and restores
    // this data will be passed to BE on new registration (Glitter)
    const ACQUISITION_ENTRY_ROUTE_KEY = 'acquisition_entry_route'
    if (!sessionStorage.getItem(ACQUISITION_ENTRY_ROUTE_KEY)) {
      sessionStorage.setItem(ACQUISITION_ENTRY_ROUTE_KEY, router.asPath)
    }
  }, [router.asPath])

  // https://codesandbox.io/s/github/tanem/react-nprogress/tree/master/examples/next-router?file=/pages/_app.tsx
  useEffect(() => {
    const handleRouteChangeStart = () => {
      setState(prevState => ({
        ...prevState,
        routeState: 'pending',
        // eslint-disable-next-line no-bitwise
        loadingKey: prevState.loadingKey ^ 1,
      }))
    }

    const handleRouteChangeEnd = () => {
      setState(prevState => ({
        ...prevState,
        routeState: 'resolved',
      }))
    }

    router.events.on('routeChangeStart', handleRouteChangeStart)
    router.events.on('routeChangeComplete', handleRouteChangeEnd)

    /** ************************** TODO **************************
     * silently failing for now, come back to see if there's a
     * need to do something better
     * **********************************************************
     */
    router.events.on('routeChangeError', handleRouteChangeEnd)

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
      router.events.off('routeChangeComplete', handleRouteChangeEnd)
      router.events.off('routeChangeError', handleRouteChangeEnd)
    }
  }, [router.events])

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // should be same as /public/service-worker-vX.js, also update CACHE_NAME version in service worker file
      navigator.serviceWorker
        .register('/service-worker-v01.js')
        .then(registration => {
          console.info('Service worker registered:', registration)
        })
        .catch(error => {
          console.info('Service worker registration failed:', error)
        })
      /*navigator
          .serviceWorker
          .getRegistration()
          .then((serviceWorker) => {
              console.log(serviceWorker);
              if (serviceWorker) {
                  serviceWorker.unregister();  
          }})
          .catch((error) => {
              console.error("There was an error: ", error);
          });*/
    }

    // override window.fetch to use api gateway endpoints
    // this will only work once whole app has been mounted
    // hence fetch-request.ts has also been modified
    const originalFetch = window.fetch
    window.fetch = (resource, config) => {
      if (typeof resource === 'string') {
        const apiGatewayUrl = getApiGatewayUrl(resource)
        return originalFetch(apiGatewayUrl, config)
      }
      return originalFetch(resource, config)
    }
  }, [])

  const isKyc = router.pathname.includes('kyc')
  const isBlogsPage = router.pathname.includes('/blog')
  const isFullScreen =
    router.pathname.includes('/stocks/fullscreen') ||
    router.pathname.includes('/stocks/advanced') ||
    router.pathname.includes('/stocks/trading')

  const logError = (error: Error) => {
    void newrelicCustomEvents({ error, customErrorType: 'error-boundary' })
  }
  const inApp = platform === 'android' || platform === 'ios'
  const gtmDisabled = host?.includes(INDSTOCKS_HOST)

  if (sisterDomain) {
    return null // this is to avoid double redirection issue - refer this doc for more info - https://docs.google.com/document/d/1jThlDRyV44kxHK_pzgvUOfgHIus4SJH-gU3qrdoGe3A/edit?tab=t.0#heading=h.y0iudnl9lq0e
  }
  const appJsx = (
    <>
      {!inApp && <GlobalExternalScripts isGtmDisabled={gtmDisabled} />}
      <SEOHeader host={host} noSchema />
      <ErrorBoundary FallbackComponent={SomethingWentWrong} onError={logError}>
        <div className="relative">
          <UserProvider>
            <ProgressLoading
              isRouteChanging={state.routeState === 'pending'}
              key={state.loadingKey}
            />
            <Head>
              <meta
                name="viewport"
                content="width=device-width,initial-scale=1"
              />
              {dnsPrefetchLinks.map(href => {
                return <link rel="dns-prefetch" href={href} key={href} />
              })}
              {isBlogsPage && (
                <link rel="dns-prefetch" href="//strapi-cdn.indmoney.com" />
              )}
            </Head>
            <CommonStateProvider>
              {/* Initializing mixpanel events for pages router */}
              {router.pathname.includes('account-aggregator') && (
                <FinvuScript />
              )}
              <AppLayout
                {...{ referrer, ...props, ...pageProps, platform, host }}
              >
                <Toast />
                <Component {...pageProps} />
              </AppLayout>
            </CommonStateProvider>
          </UserProvider>
        </div>
      </ErrorBoundary>
    </>
  )

  if (isFullScreen) {
    return (
      <>
        {/* <GlobalExternalScripts /> */}
        <NatsProviderWrapper authorization={authorization} platform={platform}>
          <FullScreenCandlesChart>
            <Component {...pageProps} />
          </FullScreenCandlesChart>
        </NatsProviderWrapper>
      </>
    )
  }

  if (
    router.pathname.includes('/stocks') ||
    router.pathname.includes('options') ||
    router.pathname.includes('dashboard')
  ) {
    return <NatsProviderWrapper>{appJsx}</NatsProviderWrapper>
  }

  if (isKyc) {
    const routeName = getRouteNameFromUrl(router.pathname)

    /**
     * @todo
     * before going live ` return <KycDataHandler>{appJsx}</KycDataHandler>`
     * should be returned and the if check for `Object.keys(allKycScreenIds).include
     * (routeName)` should be removed it is just so that in development we don't
     * have to keep all screen names in kycConfig
     */

    if (Object.keys(allKycScreenIds).includes(routeName)) {
      return (
        <>
          {/* common ui state provider is not being used anywhere but keeping it here as placeholder just in case it's needed ever */}
          <KycCommonUIStateProvider>
            <KycDataHandler routeState={state.routeState}>
              <div className="mt-10">{appJsx}</div>
            </KycDataHandler>
          </KycCommonUIStateProvider>
        </>
      )
    }

    return appJsx
  }

  return appJsx
}

// https://nextjs.org/docs/advanced-features/measuring-performance#typescript
export async function reportWebVitals(metric: NextWebVitalsMetric) {
  const isMobile = getIsMobileClient()
  const authToken = Cookies.get('token')
  const hasCookie = !!authToken

  // js drop off detection code
  if (metric.name === 'Next.js-hydration') {
    /** 🦢 TODO: see why eslint typescript is shouting here despite
     * ambient type declaration
     */
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    window.jsDropDebugReport('interactive')
  }

  const newRelicAPIUrl = getNextAPIURL(`/api/newrelic`)

  const data = {
    page: window.location.pathname,
    ...metric,
    metricValue: metric.value,
    metricName: metric.name,
    metricType: 'nextjs-vitals',
    isMobile: isMobile.toString(),
    loggedIn: hasCookie.toString(),
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (navigator.sendBeacon) {
      navigator.sendBeacon(newRelicAPIUrl, JSON.stringify(data))
    } else {
      await fetch(newRelicAPIUrl, {
        method: 'POST',
        body: JSON.stringify(data),
        keepalive: true,
      })
    }
  } catch (e: unknown) {
    // opportunit for sentry push
  }
}

// eslint-disable-next-line complexity
MyApp.getInitialProps = async (context: { ctx }) => {
  const { req, query, asPath, res } = context.ctx ?? {}
  const host = req?.headers.host
  const platform = req?.headers.platform
    ? req.headers.platform
    : query?.platform ?? ''
  const authorization = req?.headers.authorization
    ? req.headers.authorization
    : query?.authorization

  const isInMobile = getIsMobileDevice(req?.headers?.['user-agent'])

  /* other than stocks dnt allow any POD */
  const invalidPathsForStocksHost = ['/us-stocks', '/mutual-funds']

  if (host === INDSTOCKS_HOST) {
    if (
      invalidPathsForStocksHost.filter(item => asPath?.includes(item)).length
    ) {
      if (res) {
        res.statusCode = 404
        res.end('Not found')
      }
    }
  }

  const returnTo = Array.isArray(query?.return_to)
    ? query.return_to[0]
    : query?.return_to ?? ''

  const token = query?.token
  const encryptedRefreshToken = query?.refresh_token
  const sessionId = query?.session_id
  const memberId = query?.memberId
  const logout = query?.logout
  if (token && !asPath.includes('journey/2fa')) {
    try {
      const response = await fetchPOSTRequest({
        method: 'POST',
        url: `${SHIELD_URL}/public/ext/v1/encryption/decrypt`,
        data: {
          secret: token,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const decryptToken = response?.msg ?? ''
      console.info('---SETTING TOKEN---')
      setCookie('token', decryptToken, {
        req,
        res,
        // maxAge: 3600 * 6,
        secure: true,
      })
      if (encryptedRefreshToken) {
        const decryptedRefreshTokenResponse = await fetchPOSTRequest({
          method: 'POST',
          url: `${SHIELD_URL}/public/ext/v1/encryption/decrypt`,
          data: {
            secret: encryptedRefreshToken,
          },
          headers: {
            'Content-Type': 'application/json',
          },
        })
        const decryptedRefreshToken = decryptedRefreshTokenResponse?.msg ?? ''
        console.info('---SETTING REFRESH TOKEN---')
        setCookie('refresh_token', decryptedRefreshToken, {
          req,
          res,
          secure: true,
        })
      }
    } catch (e: unknown) {
      console.info(e)
    }
  }
  if (sessionId) {
    setCookie(SESSION_ID_COOKIE_NAME, sessionId, {
      req,
      res,
      secure: true,
    })
  }
  if (memberId) {
    setCookie('memberId', memberId, {
      req,
      res,
      // maxAge: 3600 * 6,
      secure: true,
    })
  }

  if (logout) {
    deleteCookie('token', { req, res })
    deleteCookie('refresh_token', { req, res })
    deleteCookie('memberId', { req, res })
    deleteCookie(SESSION_ID_COOKIE_NAME, { req, res })
  }

  const allowedDomains = [
    'indmoney.com',
    'indstocks.com',
    'www.indmoney.com',
    'www.indstocks.com',
    'beta.indmoney.com',
    'beta.indstocks.com',
  ]

  let modifiedURL = ''
  if (
    (returnTo && token && !asPath.includes('journey/2fa')) ||
    (returnTo && logout)
  ) {
    let modifiedReturnTo: string = returnTo
    const queryToPush = omit(query, [
      'return_to',
      'logout',
      'token',
      'refresh_token',
      'session_id',
    ])
    const stringifiedQueries = new URLSearchParams(queryToPush).toString()

    // To fix security issues like
    // https://www.indstocks.com/?token=a&return_to=https://evil.com
    // https://www.indmoney.com/?logout=true&return_to=https://evil.com
    // https://www.indmoney.com/?logout=true&return_to=//evil.com/indmoney.com/../
    if (
      modifiedReturnTo.toLowerCase().includes('http') ||
      modifiedReturnTo.startsWith('//') ||
      modifiedReturnTo.startsWith('/\\')
    ) {
      try {
        const parsedUrl = new URL(modifiedReturnTo)
        const redirectToHost = parsedUrl.hostname
        // safe domains are indmoney.com, indstocks.com

        // Check if the redirectToHost is exactly one of the allowed domains
        const isDomainAllowed = allowedDomains.some(
          allowedDomain => redirectToHost === allowedDomain,
        )

        if (!isDomainAllowed) {
          modifiedReturnTo = '/'
        }
      } catch (error: unknown) {
        modifiedReturnTo = '/'
      }
    }

    modifiedURL = addParameterToURL(modifiedReturnTo, stringifiedQueries)
    console.info(
      `--- COOKIES SET NOW REDIRECTING BACK TO --- ${modifiedURL} --- return_to was --- ${returnTo} --- host was --- ${host}`,
    )
    // res.writeHead(302, { location: modifiedURL }) Removed due to redirection issue - refer this doc for more info - https://docs.google.com/document/d/1jThlDRyV44kxHK_pzgvUOfgHIus4SJH-gU3qrdoGe3A/edit?tab=t.0#heading=h.y0iudnl9lq0e
    // res.end()
  }
  /* other than stocks dnt allow any POD */
  const invalidPathsForGlobalHost = ['/us-stocks', '/mutual-funds']
  if (host === INDSTOCKS_HOST) {
    if (
      invalidPathsForGlobalHost.filter(item => asPath?.includes(item)).length
    ) {
      if (res) {
        res.statusCode = 404
        res.end('Not found')
      }
    }
  }

  return {
    props: {
      // isBot: ua.isBot,
      platform: platform ? platform.toString()?.toLowerCase() : '',
      authorization,
      isInMobile,
      host,
      stagingEnv: process.env.STAGING_ENV ?? 'development', // Do not remove - can break multiple flows
      redirectTo: modifiedURL,
    },
  }
}
